import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import SwipeToDelete from '@/components/base/SwipeToDelete';
import RippleButton from '@/components/base/RippleButton';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  selected: boolean;
  products: {
    id: string;
    name: string;
    price: number;
    images: string[] | null;
  } | null;
}

interface ToastState {
  visible: boolean;
  msg: string;
  leaving: boolean;
}

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [toast, setToast] = useState<ToastState>({ visible: false, msg: '', leaving: false });
  const [qtyAnim, setQtyAnim] = useState<Record<string, boolean>>({});
  // 直接从DB读取最新积分，不依赖缓存
  const [userPoints, setUserPoints] = useState<number | null>(null); // null = 未加载

  const fetchUserPoints = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('custom_users')
      .select('points')
      .eq('id', user.id)
      .maybeSingle();
    if (data != null) setUserPoints(data.points);
  }, [user]);

  const fetchCart = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('cart_items')
      .select('*, products(id, name, price, images)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (!error && data) setItems(data as CartItem[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);
  useEffect(() => { fetchUserPoints(); }, [fetchUserPoints]);

  const showToast = useCallback((msg: string) => {
    setToast({ visible: true, msg, leaving: false });
    setTimeout(() => {
      setToast((t) => ({ ...t, leaving: true }));
      setTimeout(() => setToast({ visible: false, msg: '', leaving: false }), 280);
    }, 1800);
  }, []);

  const toggleSelect = async (item: CartItem) => {
    const newVal = !item.selected;
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, selected: newVal } : i));
    await supabase.from('cart_items').update({ selected: newVal }).eq('id', item.id);
  };

  const toggleAll = async () => {
    const all = items.every((i) => i.selected);
    const newVal = !all;
    setItems((prev) => prev.map((i) => ({ ...i, selected: newVal })));
    if (user) {
      await supabase.from('cart_items').update({ selected: newVal }).eq('user_id', user.id);
    }
  };

  const updateQty = async (item: CartItem, delta: number) => {
    const next = Math.max(1, item.quantity + delta);
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, quantity: next } : i));
    setQtyAnim((prev) => ({ ...prev, [item.id]: true }));
    setTimeout(() => setQtyAnim((prev) => ({ ...prev, [item.id]: false })), 400);
    await supabase.from('cart_items').update({ quantity: next }).eq('id', item.id);
  };

  const removeItem = async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await supabase.from('cart_items').delete().eq('id', id);
    showToast('已删除商品');
  };

  const selectedItems = items.filter((i) => i.selected);
  const totalPoints = selectedItems.reduce((sum, i) => sum + (i.products?.price ?? 0) * i.quantity, 0);
  const allSelected = items.length > 0 && items.every((i) => i.selected);
  const pointsLoaded = userPoints !== null;
  const canExchange = pointsLoaded && (userPoints as number) >= totalPoints && selectedItems.length > 0;
  const hasSelected = selectedItems.length > 0;

  // 直接跳转结算，不做积分预判（结算页会再次校验）
  const handleExchange = () => {
    if (!hasSelected) return;
    navigate('/checkout');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center max-w-[480px] mx-auto">
        <i className="ri-shopping-cart-line text-5xl text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">请先登录</p>
        <button onClick={() => navigate('/login')} className="bg-rose-500 text-white px-6 py-2.5 rounded-full text-sm cursor-pointer whitespace-nowrap">
          去登录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-36 max-w-[480px] mx-auto">
      {/* Header */}
      <LazySection animation="fadeIn" delay={0}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between px-4 h-14">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-arrow-left-line text-gray-700 text-xl" />
            </button>
            <h1 className="font-bold text-gray-900 text-base">兑换购物车</h1>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="text-sm text-gray-600 cursor-pointer whitespace-nowrap transition-colors active:text-rose-500"
            >
              {isEditing ? '完成' : '编辑'}
            </button>
          </div>
          <div className="flex items-center justify-between px-4 py-2 bg-rose-50">
            <div className="flex items-center gap-1.5">
              <i className="ri-copper-coin-line text-rose-500 text-sm" />
              <span className="text-xs text-gray-600">可用积分：</span>
              {pointsLoaded
                ? <span className="text-rose-500 font-bold text-sm">{(userPoints as number).toLocaleString()}</span>
                : <span className="text-gray-300 text-sm animate-pulse">加载中...</span>
              }
            </div>
            {totalPoints > 0 && pointsLoaded && (
              <span className={`text-xs font-medium ${canExchange ? 'text-emerald-500' : 'text-rose-500'}`}>
                {canExchange ? '积分充足' : `还差 ${(totalPoints - (userPoints as number)).toLocaleString()} 积分`}
              </span>
            )}
          </div>
        </header>
      </LazySection>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <LazySection animation="fadeUp" delay={100}>
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <div className="w-24 h-24 flex items-center justify-center mb-4">
              <i className="ri-shopping-cart-line text-6xl" />
            </div>
            <p className="text-base font-medium mb-2 text-gray-500">购物车是空的</p>
            <p className="text-sm text-gray-400 mb-6">快去挑选心仪的商品吧</p>
            <RippleButton
              onClick={() => navigate('/shop')}
              className="bg-rose-500 text-white px-8 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
              rippleColor="rgba(255,255,255,0.35)"
            >
              去兑换
            </RippleButton>
          </div>
        </LazySection>
      ) : (
        <>
          {!isEditing && (
            <div className="flex items-center justify-center gap-1.5 py-2 text-gray-400">
              <i className="ri-arrow-left-line text-xs" />
              <span className="text-xs">左滑商品可快速删除</span>
            </div>
          )}
          <div className="px-4 pt-2 space-y-3">
            {items.map((item, idx) => (
              <LazySection key={item.id} animation="fadeUp" delay={idx * 60}>
                <SwipeToDelete onDelete={() => removeItem(item.id)} disabled={isEditing}>
                  <div className="bg-white rounded-xl p-3 flex items-center gap-3">
                    <button onClick={() => toggleSelect(item)} className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${item.selected ? 'bg-rose-500 border-rose-500 scale-110' : 'border-gray-300 scale-100'}`}>
                        {item.selected && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </button>
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50">
                      <img
                        src={item.products?.images?.[0] ?? ''}
                        alt={item.products?.name}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">{item.products?.name ?? '商品'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          <span className="text-rose-500 font-bold text-base">{(item.products?.price ?? 0).toLocaleString()}</span>
                          <span className="text-rose-500 text-xs ml-0.5">积分</span>
                        </div>
                        {isEditing ? (
                          <RippleButton
                            onClick={() => removeItem(item.id)}
                            className="text-xs text-rose-500 border border-rose-200 px-3 py-1 rounded-full cursor-pointer whitespace-nowrap"
                            rippleColor="rgba(244,63,94,0.15)"
                          >
                            删除
                          </RippleButton>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQty(item, -1)} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-full cursor-pointer active:scale-90 transition-transform duration-100">
                              <i className="ri-subtract-line text-gray-600 text-sm" />
                            </button>
                            <span className={`text-sm font-bold w-5 text-center ${qtyAnim[item.id] ? 'scale-125' : ''} transition-transform`}>
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQty(item, 1)} className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-full cursor-pointer active:scale-90 transition-transform duration-100">
                              <i className="ri-add-line text-gray-600 text-sm" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </SwipeToDelete>
              </LazySection>
            ))}
            <div className="pt-2 pb-2">
              <p className="text-xs text-gray-400 text-center">— 已经到底啦 —</p>
            </div>
          </div>
        </>
      )}

      {/* Bottom Bar */}
      {!loading && items.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '64px',
            left: 0,
            right: 0,
            maxWidth: '480px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderTop: '1px solid #f3f4f6',
            padding: '12px 16px',
            zIndex: 9999,
          }}
        >
          <div className="flex items-center justify-between">
            <button onClick={toggleAll} className="flex items-center gap-2 cursor-pointer">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${allSelected ? 'bg-rose-500 border-rose-500' : 'border-gray-300'}`}>
                {allSelected && <i className="ri-check-line text-white text-xs" />}
              </div>
              <span className="text-sm text-gray-600">全选</span>
            </button>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="text-xs text-gray-500">合计：</span>
                <span className="text-rose-500 font-bold text-xl">{totalPoints.toLocaleString()}</span>
                <span className="text-rose-500 text-xs ml-0.5">积分</span>
              </div>
              <button
                onClick={() => {
                  if (!hasSelected) return;
                  navigate('/checkout');
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: hasSelected ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  backgroundColor: hasSelected ? '#f43f5e' : '#e5e7eb',
                  color: hasSelected ? '#ffffff' : '#9ca3af',
                  border: 'none',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                去兑换（{selectedItems.length}）
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Toast */}
      {toast.visible && (
        <div
          className={`fixed bottom-28 left-1/2 z-[10000] bg-gray-800/90 text-white text-sm px-5 py-2.5 rounded-full flex items-center gap-2 ${toast.leaving ? 'opacity-0' : 'opacity-100'} transition-opacity`}
          style={{ transform: 'translateX(-50%)' }}
        >
          <i className="ri-delete-bin-line text-sm" />
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default CartPage;
